import {
  Element,
  Component,
  h,
  Host,
  Prop,
  State,
  Listen,
  Method,
} from "@stencil/core";
import store from "@stores/search.store";
import { TextFieldProps } from "../../../atoms/f-text-field/f-text-field-model";

@Component({
  tag: "f-search",
  styleUrl: "f-search.scss",
  shadow: true,
})
export class FSearch {
  private debounce: number = null;
  private button: HTMLFButtonElement;
  private searchField: HTMLFSearchFieldElement;

  @Element() el: HTMLFSearchElement;

  @State() formWidth = "0";
  @State() width = "100%";

  @Prop() propertyId: TextFieldProps["propertyId"] = "main-search";
  @Prop() searchResultsUrl: string;
  @Prop() placeholder = "Product name / SKU";
  @Prop({ mutable: true, reflect: true }) open = false;
  @Prop() debounceTime = 600;
  @Prop() numberOfCharactersBeforeRequest = 3;
  @Prop() openSearchButtonAriaLabel = "Search";
  @Prop() searchQueryParamName = "text";
  @Prop() closeButtonHiddenLabel = (window as any).translations["bcp-acc"]
    .close;
  @Prop() submitSearchHiddenLabel = (window as any).translations["bcp-acc"]
    .submit;
  @Prop({ mutable: true }) value: TextFieldProps["value"];

  @Listen("searchFieldChanged")
  onSearchFieldChanged(event: CustomEvent): void {
    if (this.debounce !== null) {
      clearTimeout(this.debounce);
      this.debounce = null;
    }
    this.debounce = window.setTimeout(() => {
      this.value = event.detail.value;
      if (this.value?.length > this.numberOfCharactersBeforeRequest) {
        store.searchTerm = this.value;
      }
      if (this.value?.length === 0) {
        store.searchTerm = "";
      }
      this.debounce = null;
    }, this.debounceTime);
  }

  @Listen("searchFieldReset")
  onSearchFieldReset(): void {
    store.searchTerm = this.value;
    this.closeSearch();
  }

  @Listen("searchFieldSubmitted")
  onSearchFieldSubmitted(): void {
    this.submitSearch();
  }

  // eslint-disable-next-line @stencil/prefer-vdom-listener
  @Listen("keydown")
  handleKeydown(event: KeyboardEvent): void {
    const activeElement = this.searchField.shadowRoot.activeElement;
    if (this.open) {
      if (
        event.key === "Enter" &&
        activeElement.shadowRoot.querySelector("input").id === this.propertyId
      ) {
        this.goToSearchResultsUrl();
      }

      if (event.key === "Escape") {
        this.closeSearch();
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        // go to f-search-suggestions and handle arrows there
        (async () => {
          await customElements.whenDefined("f-search-suggestions");
          const searchSuggestions = document.querySelector(
            "f-search-suggestions"
          );
          await searchSuggestions.focusOnNthSuggestion(0);
        })();
      }

      if (
        event.shiftKey &&
        event.key === "Tab" &&
        activeElement.tagName === "F-TEXT-FIELD"
      ) {
        event.preventDefault();
        this.closeSearch();
      }

      if (
        !event.shiftKey &&
        event.key === "Tab" &&
        activeElement.tagName === "F-BUTTON" &&
        activeElement.shadowRoot
          .querySelector("button")
          .getAttribute("type") === "reset"
      ) {
        event.preventDefault();
        this.closeSearch();
      }
    }
  }

  @Method() async openSearch(): Promise<void> {
    this.open = true;
    this.value = "";
    store.searchTerm = this.value;
    store.searchOpen = this.open;
    await this.searchField.focusOnSearchField();
  }

  @Method()
  async closeSearch(): Promise<void> {
    this.open = false;
    this.value = "";
    store.searchTerm = this.value;
    store.searchOpen = this.open;
    store.products = [];
    clearTimeout(this.debounce);
    await this.focusOnOpenSearchButton();
  }

  @Method()
  async focusOnSearch(): Promise<void> {
    await this.searchField.focusOnSearchField();
  }

  @Method()
  async focusOnOpenSearchButton(): Promise<void> {
    await this.button.focusOnButton();
  }

  @Method()
  async submitSearch(): Promise<void> {
    this.goToSearchResultsUrl();
    await this.closeSearch();
  }

  componentDidRender() {
    this.calculatePosition();
  }

  @Listen("resize", { target: "window" })
  calculatePosition(): void {
    const documentWidth = document.body.clientWidth;
    const isMobile = documentWidth < 1024;
    const paddings = isMobile ? 0 : 32;
    const padding = paddings / 2;

    // 100% width on mobile
    const distanceFromLeftToContainer =
      documentWidth > 1152 ? (documentWidth - 1152) / 2 + padding : padding;
    const distanceFromLeftToFSearch = document
      .querySelector("f-search")
      .getBoundingClientRect().x;

    // distance from left of screen till beginning of search - distance from left to container + search icon width
    this.width = isMobile
      ? "100%"
      : distanceFromLeftToFSearch - distanceFromLeftToContainer + 50 + "px";
  }

  private goToSearchResultsUrl = (): void => {
    // it turns out that the data binding in the stencil is so slow (takes about 1s in case of pasting text into a field)
    // so it is safer to get the value directly out of the element.
    const paramValue = this.searchField.shadowRoot
      .querySelector("f-text-field")
      .shadowRoot.querySelector("input").value;
    window.location.href = `${this.searchResultsUrl}?${
      this.searchQueryParamName
    }=${encodeURIComponent(paramValue)}`;
  };

  private handleOpenSearch = (): void => {
    (async () => {
      await customElements.whenDefined("f-search");
      await this.openSearch();
    })();
  };

  render() {
    return (
      <Host>
        <div
          class={{
            "o-header__search": true,
            "is-open": this.open,
          }}
          data-test="search-wrapper"
        >
          <form
            class="m-search-form"
            autoComplete="off"
            style={{}}
            data-test="search-form"
          >
            <div class="m-search-form__inner" data-test="search-form-inner">
              <div style={{ width: this.width }}>
                <f-search-field
                  propertyId={this.propertyId}
                  placeholder={this.placeholder}
                  value={this.value}
                  ref={(el) => (this.searchField = el)}
                  closeButtonHiddenLabel={this.closeButtonHiddenLabel}
                  submitSearchHiddenLabel={this.submitSearchHiddenLabel}
                />
              </div>
            </div>
          </form>
          <f-button
            class={this.open ? "open" : ""}
            variant="primary"
            iconOnly={true}
            additionalProps={{
              "aria-label": this.openSearchButtonAriaLabel,
              "aria-haspopup": "true",
              "aria-expanded": this.open ? "true" : "false",
              "aria-controls": this.propertyId,
            }}
            onClick={this.handleOpenSearch}
            ref={(el) => (this.button = el)}
            data-test="search-button"
          >
            <f-icon iconName="search" />
          </f-button>
        </div>
      </Host>
    );
  }
}
